// The classifier: what kind of blunder or miss a move was, from the facts
// the scan left (`MistakeFact`, written by `@chess/api-insights`' `mistakes`
// extractor). Every rule reads the stored positions and lines, so a better
// rule needs no rescan, only a reload.
//
// - severity: how much of the reader's chance of winning the move gave
//   up, on the expected-score curve rather than in pawns, so +8 to +5 is
//   minor and +1 to −1 is not;
// - swing: the standing before and after (winning … losing);
// - material: what the engine's reply wins (a blunder) or what the best line
//   would have won (a miss);
// - pattern: allowed or missed mate, a piece left hanging (or a free
//   piece missed), a fork walked into (or missed), another tactic that cost
//   material, or a positional error;
// - context: the opening's variation in the opening, the kind of ending in
//   the ending, the pattern in the middlegame.
import { ENDING_TYPE_LABEL, endingTypeOf } from '@chess/api-ending-case';
import { type Color, type EndingType, type GameFacts, type LostPiece, type MistakeFact, type MoveKind, type Phase } from '@chess/api-model';
import { Board, opposite, VALUE, type PieceKind, type Square } from './board.ts';

export type Severity = 'decisive' | 'serious' | 'minor';
export type Standing = 'winning' | 'better' | 'equal' | 'worse' | 'losing';
export type Material = LostPiece | 'none';
export type Pattern = 'mate' | 'hanging' | 'fork' | 'tactic' | 'positional';

export const SEVERITIES = ['decisive', 'serious', 'minor'] as const satisfies readonly Severity[];
export const PATTERNS = ['mate', 'hanging', 'fork', 'tactic', 'positional'] as const satisfies readonly Pattern[];
export const MATERIALS = ['queen', 'rook', 'bishop', 'knight', 'pawn', 'mate', 'none'] as const satisfies readonly Material[];
export const STAGES = ['opening', 'middlegame', 'ending'] as const satisfies readonly Phase[];

/** A drop in winning chance (0 … 1) this large or more is decisive; at least `SERIOUS_DROP`, serious; less, minor. */
export const DECISIVE_DROP = 0.3;
export const SERIOUS_DROP = 0.15;

export const STAGE_LABEL: Record<Phase, string> = { opening: 'Opening', middlegame: 'Middlegame', ending: 'Ending' };
export const SEVERITY_LABEL: Record<Severity, string> = { decisive: 'Decisive', serious: 'Serious', minor: 'Minor' };
export const STANDING_LABEL: Record<Standing, string> = { winning: 'Winning', better: 'Better', equal: 'Equal', worse: 'Worse', losing: 'Losing' };
/** The kinds of ending and their names are `@chess/api-ending-case`'s, shared with the ending cards and the library filter. */
export const ENDING_LABEL = ENDING_TYPE_LABEL;
export { endingTypeOf, type EndingType };
export const PATTERN_LABEL: Record<MoveKind, Record<Pattern, string>> = {
  blunder: { mate: 'Allowed mate', hanging: 'Piece left hanging', fork: 'Walked into a fork', tactic: 'Other tactic', positional: 'Positional' },
  miss: { mate: 'Missed mate', hanging: 'Missed a free piece', fork: 'Missed a fork', tactic: 'Missed material', positional: 'Missed a better line' },
};
export const MATERIAL_LABEL: Record<MoveKind, Record<Material, string>> = {
  blunder: { queen: 'Queen', rook: 'Rook', bishop: 'Bishop', knight: 'Knight', pawn: 'Pawn', mate: 'Allowed mate', none: 'No material' },
  miss: { queen: 'Queen', rook: 'Rook', bishop: 'Bishop', knight: 'Knight', pawn: 'Pawn', mate: 'Mate', none: 'No material' },
};

/** The reader's chance of winning at a score in pawns, on the curve Lichess uses for its accuracy: 0.5 when level. */
export function winChance(pawns: number): number {
  return 1 / (1 + Math.exp(-0.368208 * pawns));
}

export function severityOf(before: number, after: number): Severity {
  const drop = winChance(before) - winChance(after);
  return drop >= DECISIVE_DROP ? 'decisive' : drop >= SERIOUS_DROP ? 'serious' : 'minor';
}

export function swingStandingOf(pawns: number): Standing {
  if (pawns >= 2) return 'winning';
  if (pawns >= 0.5) return 'better';
  if (pawns > -0.5) return 'equal';
  if (pawns > -2) return 'worse';
  return 'losing';
}


/** A capture of `victim`'s piece that wins it: the piece stood undefended, or is worth more than what takes it. */
function winningCapture(board: Board, uci: string | undefined, victim: Color): { square: Square; piece: PieceKind } | undefined {
  if (!uci) return undefined;
  const mover = board.get(uci.slice(0, 2));
  const taken = board.captured(uci);
  if (!mover || !taken || taken.colour !== victim || taken.kind === 'king') return undefined;
  const after = board.play(uci);
  const defended = after.attackers(uci.slice(2, 4), victim).length > 0;
  return !defended || VALUE[taken.kind] > VALUE[mover.kind] ? { square: taken.square, piece: taken.kind } : undefined;
}

/**
 * A fork: after `uci`, the piece that moved attacks two or more of
 * `victim`'s pieces each of which it would win: the king, a piece worth more
 * than itself, or one left undefended.
 */
function fork(board: Board, uci: string | undefined, victim: Color): { square: Square; piece: PieceKind; targets: Square[] } | undefined {
  if (!uci) return undefined;
  const to = uci.slice(2, 4);
  const after = board.play(uci);
  const forker = after.get(to);
  if (!forker || forker.colour === victim) return undefined;
  const targets = after.attacksFrom(to).filter((sq) => {
    const p = after.get(sq);
    if (!p || p.colour !== victim || p.kind === 'pawn') return false;
    return p.kind === 'king' || VALUE[p.kind] > VALUE[forker.kind] || after.attackers(sq, victim).length === 0;
  });
  return targets.length >= 2 ? { square: to, piece: forker.kind, targets } : undefined;
}

export interface PatternFinding {
  pattern: Pattern;
  /** The piece left hanging or missed, or the forking piece. */
  piece?: PieceKind;
  /** Its square. */
  square?: Square;
}

/** The pattern of a blunder (from the opponent's best reply) or a miss (from the reader's best line). */
export function patternOf(m: MistakeFact, reader: Color): PatternFinding {
  if (m.kind === 'blunder') {
    if (m.lost === 'mate' || (m.reply?.mate ?? 0) > 0) return { pattern: 'mate' };
    const board = Board.fromFen(m.fenAfter);
    const reply = m.reply?.pv[0];
    const hanging = winningCapture(board, reply, reader);
    if (hanging) return { pattern: 'hanging', ...hanging };
    const forked = fork(board, reply, reader);
    if (forked) return { pattern: 'fork', piece: forked.piece, square: forked.square };
    return { pattern: m.lost ? 'tactic' : 'positional' };
  }
  if (m.missed === 'mate' || (m.best?.mate ?? 0) > 0) return { pattern: 'mate' };
  const board = Board.fromFen(m.fenBefore);
  const best = m.best?.pv[0];
  const free = winningCapture(board, best, opposite(reader));
  if (free) return { pattern: 'hanging', ...free };
  const forked = fork(board, best, opposite(reader));
  if (forked) return { pattern: 'fork', piece: forked.piece, square: forked.square };
  return { pattern: m.missed ? 'tactic' : 'positional' };
}

/** What a blunder gave away (the reply's net material) or a miss passed over (the best line's), or `none`. */
export function materialOf(m: Pick<MistakeFact, 'kind' | 'lost' | 'missed'>): Material {
  return (m.kind === 'blunder' ? m.lost : m.missed) ?? 'none';
}

/** The game a mistake belongs to, as the list shows it. */
export type MistakeGame = Pick<GameFacts, 'id' | 'playedAt' | 'colour' | 'opponent' | 'reviewPath' | 'variation' | 'opening'>;

export interface ClassifiedMistake {
  gameId: string;
  playedAt: number;
  opponent?: string;
  /** The review at this move's puzzle, when the game names its source. */
  practicePath?: string;
  ply: number;
  /** The move numbered, e.g. "23.Qxb7" or "23…Qxb7". */
  move: string;
  kind: MoveKind;
  stage: Phase;
  severity: Severity;
  swing: { from: Standing; to: Standing };
  before: number;
  after: number;
  material: Material;
  pattern: Pattern;
  /** The pattern as the list names it for this kind, e.g. "Piece left hanging". */
  patternLabel: string;
  /** The opening's variation, the kind of ending, or the pattern in the middlegame. */
  context: string;
  /** The engine's better move, in SAN. */
  best?: string;
  /** One sentence for the reader. */
  description: string;
}

const PIECE_WORD: Record<PieceKind, string> = { pawn: 'pawn', knight: 'knight', bishop: 'bishop', rook: 'rook', queen: 'queen', king: 'king' };
const numbered = (ply: number, san: string) => `${Math.ceil(ply / 2)}${ply % 2 ? '.' : '…'}${san}`;
const pawns = (p: number) => `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p).toFixed(2)}`;

function sentence(m: MistakeFact, found: PatternFinding, material: Material): string {
  const piece = found.piece ? PIECE_WORD[found.piece] : undefined;
  const what = material !== 'none' && material !== 'mate' ? PIECE_WORD[material] : undefined;
  if (m.kind === 'blunder') {
    switch (found.pattern) {
      case 'mate':
        return 'allowed a forced mate';
      case 'hanging':
        return `left your ${piece} on ${found.square} to be taken`;
      case 'fork':
        return `walked into a ${piece} fork on ${found.square}`;
      case 'tactic':
        return `gave away a ${what}`;
      case 'positional':
        return 'gave away the position';
    }
  }
  switch (found.pattern) {
    case 'mate':
      return 'missed a forced mate';
    case 'hanging':
      return `missed the free ${piece} on ${found.square}`;
    case 'fork':
      return `missed a ${piece} fork on ${found.square}`;
    case 'tactic':
      return `missed winning a ${what}`;
    case 'positional':
      return 'missed a clearly better line';
  }
}

/** Classifies one mistake of the reader's in one of their games. */
export function classify(m: MistakeFact, game: MistakeGame): ClassifiedMistake {
  const reader = game.colour;
  const found = patternOf(m, reader);
  const material = materialOf(m);
  const bestUci = m.best?.pv[0];
  const best = bestUci ? Board.fromFen(m.fenBefore).san(bestUci, m.best?.mate === 1) : undefined;
  const patternLabel = PATTERN_LABEL[m.kind][found.pattern];
  const context =
    m.stage === 'opening' ? (game.variation ?? game.opening ?? 'Opening') : m.stage === 'ending' ? ENDING_LABEL[endingTypeOf(m.fenBefore)] : patternLabel;
  const move = numbered(m.ply, m.san);
  const description = `${move}${m.kind === 'blunder' ? '??' : '?!'} ${sentence(m, found, material)}: ${pawns(m.before)} to ${pawns(m.after)}.${best ? ` Better: ${best}.` : ''}`;
  return {
    gameId: game.id,
    playedAt: game.playedAt,
    opponent: game.opponent,
    practicePath: game.reviewPath ? `${game.reviewPath}?puzzle=${m.ply}#ply=${m.ply - 1}` : undefined,
    ply: m.ply,
    move,
    kind: m.kind,
    stage: m.stage,
    severity: severityOf(m.before, m.after),
    swing: { from: swingStandingOf(m.before), to: swingStandingOf(m.after) },
    before: m.before,
    after: m.after,
    material,
    pattern: found.pattern,
    patternLabel,
    context,
    best,
    description,
  };
}

/** Newest game first; within a game, in move order. */
export function byRecency(a: ClassifiedMistake, b: ClassifiedMistake): number {
  return b.playedAt - a.playedAt || a.ply - b.ply;
}
